import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { EventinfoBase } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputText,
    ValidatedText,
    InputOrganizerSelect,
    InputInt,
    ValidatedInt,
    InputProfileSelect,
    InputNullableInt,
    InputDateTime,
    DateComp,
    DisplaySeparator,
    InputSelect,
    DataSourceLookup,
} from '@/components';
import { type DateType } from '@/utilities/dateutils';
import { useCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';
import { useValidator } from '@/composeables';

export class EventinfoBaseGuiDef extends GuiDef<EventinfoBase> {
    constructor() {
        super('EventinfoBase');

        const Common = storeToRefs(useCommonStore());
        const FederationsSource = computed(() => {
            return Common.FederationsAsSelectLine.value;
        });
        const FederationsSourceFilter = (obj: EventinfoBase) => {
            const valuesToInclude = Common.Federations.value
                .filter((x) => Common.Organizers.value.find((z) => z.ID == obj.OrganizerID)?.CountryID == x.CountryID)
                .map((x) => x.ID as SelectLineValueType);

            // update if field does not have the correct value
            if (!valuesToInclude.includes(obj.FederationID)) {
                if (valuesToInclude.length > 0) {
                    obj.FederationID = valuesToInclude[0] as number;
                } else {
                    console.warn('obj.FederationID not set as we have no data in source');
                }
            }

            return Array.from(FederationsSource.value.values()).filter((x) =>
                valuesToInclude.map((x) => x as SelectLineValueType).includes(x.value),
            );
        };
        const SportsSource = computed(() => {
            return Common.SportsAsSelectLine.value;
        });
        const SportsSourceFilter = (obj: EventinfoBase) => {
            const valuesToInclude = Common.Sports.value
                .filter((x) => x.FederationID == obj.FederationID)
                .map((x) => x.ID as SelectLineValueType);

            // update if field does not have the correct value
            if (!valuesToInclude.includes(obj.SportID)) {
                if (valuesToInclude.length > 0) {
                    obj.SportID = valuesToInclude[0] as number;
                } else {
                    console.warn('obj.SportID not set as we have no data in source');
                }
            }

            return Array.from(SportsSource.value.values()).filter((x) =>
                valuesToInclude.map((x) => x as SelectLineValueType).includes(x.value),
            );
        };
        const SportLevelsSource = computed(() => {
            return Common.SportLevelsAsSelectLine.value;
        });
        const SportLevelsSourceFilter = (obj: EventinfoBase) => {
            const valuesToInclude = Common.SportLevels.value
                .filter((x) => x.SportID == obj.SportID)
                .map((x) => x.ID as SelectLineValueType);

            // update if field does not have the correct value
            if (!valuesToInclude.includes(obj.SportlevelID)) {
                if (valuesToInclude.length > 0) {
                    obj.SportlevelID = valuesToInclude[0] as number | null;
                } else {
                    console.warn('obj.SportlevelID not set as we have no data in source');
                }
            }

            return Array.from(SportLevelsSource.value.values()).filter((x) =>
                valuesToInclude.map((x) => x as SelectLineValueType).includes(x.value),
            );
        };
        const DisciplinesSource = computed(() => {
            return Common.DisciplinesAsSelectLine.value;
        });
        const DisciplinesSourceFilter = (obj: EventinfoBase) => {
            const valuesToInclude = Common.Disciplines.value
                .filter((x) => x.SportID == obj.SportID)
                .map((x) => x.ID as SelectLineValueType);

            // update if field does not have the correct value
            if (!valuesToInclude.includes(obj.DisciplineID)) {
                if (valuesToInclude.length > 0) {
                    obj.DisciplineID = valuesToInclude[0] as number | null;
                } else {
                    console.warn('obj.DisciplineID not set as we have no data in source');
                }
            }

            return Array.from(DisciplinesSource.value.values()).filter((x) =>
                valuesToInclude.map((x) => x as SelectLineValueType).includes(x.value),
            );
        };

        const NameValidator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        const Name = new FieldDef<EventinfoBase>();
        Name.Name = 'Name';
        Name.FieldName = 'Name';
        Name.DefinedFor = 'Both';
        Name.TranslationKey = 'eventinfobase.name';
        Name.Editable = true;
        Name.Filterable = true;
        Name.Sortable = true;
        Name.CssClasses = '';
        Name.value = (eventinfobase) => eventinfobase.Name;
        Name.setValue = (eventinfobase, value) => (eventinfobase.Name = value);
        Name.DetailEditor = markRaw(InputText);
        Name.DetailEditorProps = { maxlength: 100 };
        Name.CellEditor = markRaw(InputText);
        Name.CellEditorProps = {};
        Name.CellRenderer = markRaw(ValidatedText);
        Name.CellRendererProps = {};
        Name.Validator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        Name.SortDirection = undefined;
        Name.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(NameValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(NameValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        Name.MinWidth = undefined;
        Name.MaxWidth = undefined;
        Name.Tooltip = null;
        this.Fields.push(Name);

        const OrganizerIDValidator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        const OrganizerID = new FieldDef<EventinfoBase>();
        OrganizerID.Name = 'OrganizerID';
        OrganizerID.FieldName = 'OrganizerID';
        OrganizerID.DefinedFor = 'Both';
        OrganizerID.TranslationKey = 'eventinfobase.organizerid';
        OrganizerID.Editable = true;
        OrganizerID.Filterable = false;
        OrganizerID.Sortable = true;
        OrganizerID.CssClasses = '';
        OrganizerID.value = (eventinfobase) => eventinfobase.OrganizerID;
        OrganizerID.setValue = (eventinfobase, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            eventinfobase.OrganizerID = value;
        };
        OrganizerID.DetailEditor = markRaw(InputOrganizerSelect);
        OrganizerID.DetailEditorProps = {};
        OrganizerID.CellEditor = markRaw(InputInt);
        OrganizerID.CellEditorProps = {};
        OrganizerID.CellRenderer = markRaw(ValidatedInt);
        OrganizerID.CellRendererProps = {};
        OrganizerID.Validator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        OrganizerID.SortDirection = undefined;
        OrganizerID.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(OrganizerIDValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(OrganizerIDValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        OrganizerID.MinWidth = undefined;
        OrganizerID.MaxWidth = undefined;
        OrganizerID.Tooltip = null;
        this.Fields.push(OrganizerID);

        const ProfileIDValidator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        const ProfileID = new FieldDef<EventinfoBase>();
        ProfileID.Name = 'ProfileID';
        ProfileID.FieldName = 'ProfileID';
        ProfileID.DefinedFor = 'Both';
        ProfileID.TranslationKey = 'eventinfobase.profileid';
        ProfileID.Editable = true;
        ProfileID.Filterable = false;
        ProfileID.Sortable = true;
        ProfileID.CssClasses = '';
        ProfileID.value = (eventinfobase) => eventinfobase.ProfileID;
        ProfileID.setValue = (eventinfobase, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            eventinfobase.ProfileID = value;
        };
        ProfileID.DetailEditor = markRaw(InputProfileSelect);
        ProfileID.DetailEditorProps = {};
        ProfileID.CellEditor = markRaw(InputNullableInt);
        ProfileID.CellEditorProps = {};
        ProfileID.CellRenderer = markRaw(ValidatedInt);
        ProfileID.CellRendererProps = {};
        ProfileID.Validator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        ProfileID.SortDirection = undefined;
        ProfileID.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(ProfileIDValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(ProfileIDValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        ProfileID.MinWidth = undefined;
        ProfileID.MaxWidth = undefined;
        ProfileID.Tooltip = null;
        this.Fields.push(ProfileID);

        const DateValidator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        const Date = new FieldDef<EventinfoBase>();
        Date.Name = 'Date';
        Date.FieldName = 'Date';
        Date.DefinedFor = 'Both';
        Date.TranslationKey = 'eventinfobase.date';
        Date.Editable = true;
        Date.Filterable = true;
        Date.Sortable = true;
        Date.CssClasses = '';
        Date.value = (eventinfobase) => eventinfobase.Date;
        Date.setValue = (eventinfobase, value) => (eventinfobase.Date = value);
        Date.DetailEditor = markRaw(InputDateTime);
        Date.DetailEditorProps = { format: 'DATE' as DateType };
        Date.CellEditor = markRaw(InputDateTime);
        Date.CellEditorProps = { format: 'DATE' as DateType };
        Date.CellRenderer = markRaw(DateComp);
        Date.CellRendererProps = { format: 'DATE' as DateType };
        Date.Validator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        Date.SortDirection = undefined;
        Date.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(DateValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(DateValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        Date.MinWidth = 100;
        Date.MaxWidth = undefined;
        Date.Tooltip = null;
        this.Fields.push(Date);

        const separator0 = new FieldDef<EventinfoBase>();
        separator0.Name = 'separator0';
        separator0.FieldName = 'separator0';
        separator0.DefinedFor = 'Detail';
        separator0.TranslationKey = 'event.eventtype';
        separator0.Editable = true;
        separator0.Filterable = false;
        separator0.Sortable = true;
        separator0.CssClasses = '';
        separator0.value = () => {
            return 'event.eventtype';
        };
        separator0.setValue = () => {};
        separator0.DetailEditor = markRaw(DisplaySeparator);
        separator0.DetailEditorProps = {};
        separator0.CellEditor = markRaw(DisplaySeparator);
        separator0.CellEditorProps = {};
        separator0.CellRenderer = markRaw(DisplaySeparator);
        separator0.CellRendererProps = {};
        separator0.Validator = null;
        separator0.SortDirection = undefined;
        separator0.cellClassRules = undefined;
        separator0.MinWidth = undefined;
        separator0.MaxWidth = undefined;
        separator0.Tooltip = null;
        this.Fields.push(separator0);

        const FederationIDValidator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        const FederationID = new FieldDef<EventinfoBase>();
        FederationID.Name = 'FederationID';
        FederationID.FieldName = 'FederationID';
        FederationID.DefinedFor = 'Both';
        FederationID.TranslationKey = 'eventinfobase.federationid';
        FederationID.Editable = true;
        FederationID.Filterable = true;
        FederationID.Sortable = true;
        FederationID.CssClasses = '';
        FederationID.value = (eventinfobase) => eventinfobase.FederationID;
        FederationID.setValue = (eventinfobase, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            eventinfobase.FederationID = value;
        };
        FederationID.DetailEditor = markRaw(InputSelect);
        FederationID.DetailEditorProps = { datasource: FederationsSourceFilter };
        FederationID.CellEditor = markRaw(InputSelect);
        FederationID.CellEditorProps = { datasource: FederationsSourceFilter };
        // @ts-expect-error @ts-ignore
        FederationID.CellRenderer = markRaw(DataSourceLookup);
        FederationID.CellRendererProps = { datasource: FederationsSourceFilter };
        FederationID.ExportValueFormatter = (obj: EventinfoBase) => {
            return FederationsSource.value.get(obj.FederationID)?.text;
        };
        FederationID.Validator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        FederationID.SortDirection = undefined;
        FederationID.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(FederationIDValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(FederationIDValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        FederationID.MinWidth = undefined;
        FederationID.MaxWidth = undefined;
        FederationID.Tooltip = null;
        this.Fields.push(FederationID);

        const SportIDValidator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        const SportID = new FieldDef<EventinfoBase>();
        SportID.Name = 'SportID';
        SportID.FieldName = 'SportID';
        SportID.DefinedFor = 'Both';
        SportID.TranslationKey = 'eventinfobase.sportid';
        SportID.Editable = true;
        SportID.Filterable = true;
        SportID.Sortable = true;
        SportID.CssClasses = '';
        SportID.value = (eventinfobase) => eventinfobase.SportID;
        SportID.setValue = (eventinfobase, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            eventinfobase.SportID = value;
        };
        SportID.DetailEditor = markRaw(InputSelect);
        SportID.DetailEditorProps = { datasource: SportsSourceFilter };
        SportID.CellEditor = markRaw(InputSelect);
        SportID.CellEditorProps = { datasource: SportsSourceFilter };
        // @ts-expect-error @ts-ignore
        SportID.CellRenderer = markRaw(DataSourceLookup);
        SportID.CellRendererProps = { datasource: SportsSourceFilter };
        SportID.ExportValueFormatter = (obj: EventinfoBase) => {
            return SportsSource.value.get(obj.SportID)?.text;
        };
        SportID.Validator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        SportID.SortDirection = undefined;
        SportID.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(SportIDValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(SportIDValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        SportID.MinWidth = undefined;
        SportID.MaxWidth = undefined;
        SportID.Tooltip = null;
        this.Fields.push(SportID);

        const SportlevelID = new FieldDef<EventinfoBase>();
        SportlevelID.Name = 'SportlevelID';
        SportlevelID.FieldName = 'SportlevelID';
        SportlevelID.DefinedFor = 'Both';
        SportlevelID.TranslationKey = 'eventinfobase.sportlevelid';
        SportlevelID.Editable = true;
        SportlevelID.Filterable = true;
        SportlevelID.Sortable = true;
        SportlevelID.CssClasses = '';
        SportlevelID.value = (eventinfobase) => eventinfobase.SportlevelID;
        SportlevelID.setValue = (eventinfobase, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            eventinfobase.SportlevelID = value;
        };
        SportlevelID.DetailEditor = markRaw(InputSelect);
        SportlevelID.DetailEditorProps = { datasource: SportLevelsSourceFilter };
        SportlevelID.CellEditor = markRaw(InputSelect);
        SportlevelID.CellEditorProps = { datasource: SportLevelsSourceFilter };
        // @ts-expect-error @ts-ignore
        SportlevelID.CellRenderer = markRaw(DataSourceLookup);
        SportlevelID.CellRendererProps = { datasource: SportLevelsSourceFilter };
        SportlevelID.ExportValueFormatter = (obj: EventinfoBase) => {
            return SportLevelsSource.value.get(obj.SportlevelID)?.text;
        };
        SportlevelID.Validator = null;
        SportlevelID.SortDirection = undefined;
        SportlevelID.cellClassRules = undefined;
        SportlevelID.MinWidth = undefined;
        SportlevelID.MaxWidth = undefined;
        SportlevelID.Tooltip = null;
        this.Fields.push(SportlevelID);

        const DisciplineID = new FieldDef<EventinfoBase>();
        DisciplineID.Name = 'DisciplineID';
        DisciplineID.FieldName = 'DisciplineID';
        DisciplineID.DefinedFor = 'Both';
        DisciplineID.TranslationKey = 'eventinfobase.disciplineid';
        DisciplineID.Editable = true;
        DisciplineID.Filterable = true;
        DisciplineID.Sortable = true;
        DisciplineID.CssClasses = '';
        DisciplineID.value = (eventinfobase) => eventinfobase.DisciplineID;
        DisciplineID.setValue = (eventinfobase, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            eventinfobase.DisciplineID = value;
        };
        DisciplineID.DetailEditor = markRaw(InputSelect);
        DisciplineID.DetailEditorProps = { datasource: DisciplinesSourceFilter };
        DisciplineID.CellEditor = markRaw(InputSelect);
        DisciplineID.CellEditorProps = { datasource: DisciplinesSourceFilter };
        // @ts-expect-error @ts-ignore
        DisciplineID.CellRenderer = markRaw(DataSourceLookup);
        DisciplineID.CellRendererProps = { datasource: DisciplinesSourceFilter };
        DisciplineID.ExportValueFormatter = (obj: EventinfoBase) => {
            return DisciplinesSource.value.get(obj.DisciplineID)?.text;
        };
        DisciplineID.Validator = null;
        DisciplineID.SortDirection = undefined;
        DisciplineID.cellClassRules = undefined;
        DisciplineID.MinWidth = undefined;
        DisciplineID.MaxWidth = undefined;
        DisciplineID.Tooltip = null;
        this.Fields.push(DisciplineID);
    }

    public async LoadDependencies() {
        await useCommonStore().EnsureLoaded(['Organizers']);
        await useCommonStore().EnsureLoaded(['Federations']);
    }
}
